/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { globalAlert } from "../../actions/commonActions";
import {
  lmsQuizPreviewApi,
  lmsGradeQuizPreviewApi,
  lmsGetQuizApi,
} from "../../admin/_lmsActions";
import {
  learnerQuizStartApi,
  learnerQuizDraftApi,
  learnerQuizSubmitApi,
} from "../../lms/_learnerActions";

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};
const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

// True when a question allows multiple correct answers. Prefers the explicit
// questionType; falls back to inferring from the answer key (client mode) or to
// multi-select when nothing is known (server payloads strip correctness).
const isMulti = (q, serverMode) => {
  if (q.questionType) return q.questionType === "multiple_choice";
  if (serverMode) return true;
  return (q.options || []).filter((o) => o.isCorrect).length > 1;
};

const fmtClock = (s) => {
  const m = Math.floor(s / 60);
  const ss = String(Math.max(0, s % 60)).padStart(2, "0");
  return `${m}:${ss}`;
};

const fmtElapsed = (seconds) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return null;
  const mins = Math.floor(s / 60);
  const secs = Math.round(s % 60);
  return mins ? `${mins}m ${String(secs).padStart(2, "0")}s` : `${secs}s`;
};

const fmtTimestamp = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
  return `${day}/${month}/${year}, ${time}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Single shared quiz runner — ONE quiz UI for the whole app. Three modes:
//   • quizId        → admin preview: server-loaded questions, preview-graded,
//                     nothing persisted.
//   • quiz          → client mode: in-memory questions + grading (unsaved editor).
//   • learnerQuizId → LEARNER mode: real attempt via POST /quiz/start (resumes an
//                     open attempt with saved draftAnswers), debounced draft
//                     autosave, optional countdown (timeLimitMinutes → auto-submit
//                     at 0), atomic submit with feedback (hints + explanations),
//                     pass/fail + course-completion callback. 204 soft errors
//                     (max attempts / locked) render inline as a blocked state.
// Renders by questionType: single_choice / true_false → radio, multiple_choice →
// checkbox. Quiz title/description render only when present.
// ─────────────────────────────────────────────────────────────────────────────
const LmsQuizRunner = ({
  quizId,
  quiz,
  learnerQuizId,
  enrolmentId,
  quizMeta, // optional { title, description, maxAttempts } shown in learner mode
  onComplete,
}) => {
  const { t } = useTranslation();
  const learnerMode = !!learnerQuizId;
  const serverMode = !!quizId && !learnerMode;
  const previewMode = !learnerMode;

  const [questions, setQuestions] = useState([]);
  const [passMark, setPassMark] = useState(0);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [showSolution, setShowSolution] = useState(false);
  const [solutionIndex, setSolutionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // learner-mode state
  const [attempt, setAttempt] = useState(null);
  const [blocked, setBlocked] = useState(""); // 204 soft error (locked / max attempts)
  const [remaining, setRemaining] = useState(null); // seconds left on timed quizzes
  const timerRef = useRef(null);
  const draftTimer = useRef(null);
  const submittingRef = useRef(false);
  const startedAtRef = useRef(Date.now());

  const reset = () => {
    startedAtRef.current = Date.now();
    setIdx(0);
    setAnswers({});
    setResult(null);
    setShowSolution(false);
    setSolutionIndex(0);
  };

  const buildClient = () => {
    const qs = shuffle(quiz?.questions || []).map((q, qi) => {
      const opts = (q.options || []).map((o, oi) => ({
        id: `${qi}-${oi}`,
        text: o.text,
        isCorrect: !!o.isCorrect,
        hint: o.hint || "",
        explanation: o.explanation || o.hint || "",
      }));
      const norm = {
        questionText: q.questionText,
        marks: Number(q.marks) || 1,
        questionType: q.questionType,
        explanation: q.explanation || "",
      };
      return { id: `${qi}`, ...norm, multi: isMulti(norm.questionType ? norm : q, false), options: shuffle(opts) };
    });
    setQuestions(qs);
    setPassMark(Number(quiz?.passMark) || 0);
    reset();
  };

  const mapServerQuestions = (list) =>
    (list || []).map((qq) => ({
      id: qq._id || qq.questionId,
      questionText: qq.questionText,
      marks: Number(qq.marks) || 1,
      questionType: qq.questionType,
      multi: isMulti(qq, true),
      options: (qq.options || []).map((o) => ({
        id: o._id,
        text: o.text,
        hint: o.hint || "",
        explanation: o.explanation || o.hint || "",
      })),
    }));

  const mergeAnswerKey = (previewQuestions, fullQuestions = []) => {
    const byId = {};
    fullQuestions.forEach((question) => {
      const id = question?._id || question?.questionId;
      if (id) byId[String(id)] = question;
    });

    return previewQuestions.map((question) => {
      const fullQuestion =
        byId[String(question.id)] ||
        fullQuestions.find((item) => item?.questionText === question.questionText) ||
        {};
      const fullOptions = fullQuestion.options || [];

      return {
        ...question,
        explanation: fullQuestion.explanation || question.explanation || "",
        options: question.options.map((option, optionIndex) => {
          const fullOption =
            fullOptions.find((item) => String(item?._id || item?.id) === String(option.id)) ||
            fullOptions.find((item) => item?.text === option.text) ||
            fullOptions[optionIndex] ||
            {};

          return {
            ...option,
            isCorrect: !!fullOption.isCorrect,
            hint: fullOption.hint || option.hint || "",
            explanation:
              fullOption.explanation ||
              fullOption.hint ||
              option.explanation ||
              option.hint ||
              "",
          };
        }),
      };
    });
  };

  const loadServer = async () => {
    setLoading(true);
    try {
      const res = await lmsQuizPreviewApi(quizId);
      const q = res?.data?.data?.quiz || res?.data?.data || {};
      setPassMark(Number(q?.passMark) || 0);
      const previewQuestions = mapServerQuestions(q?.questions);
      try {
        const fullRes = await lmsGetQuizApi(quizId);
        const fullQuiz = fullRes?.data?.data?.quiz || fullRes?.data?.data || {};
        setQuestions(mergeAnswerKey(previewQuestions, fullQuiz?.questions || []));
      } catch {
        setQuestions(previewQuestions);
      }
      reset();
    } catch (e) {
      globalAlert("error", e?.response?.data?.message);
    }
    setLoading(false);
  };

  // Learner mode — start (or resume) a real attempt.
  const loadLearner = async () => {
    setLoading(true);
    setBlocked("");
    stopTimer();
    try {
      const res = await learnerQuizStartApi(learnerQuizId, enrolmentId);
      const { status, data, message } = res?.data || {};
      if (status === 200) {
        const at = data?.attempt || data || {};
        setAttempt(at);
        setPassMark(Number(at?.passMark) || 0);
        const qs = mapServerQuestions(at?.questions);
        setQuestions(qs);
        reset();
        const started = at?.startedAt ? new Date(at.startedAt).getTime() : NaN;
        startedAtRef.current = Number.isNaN(started) ? Date.now() : started;
        // Seed saved draft answers on resumed attempts.
        const draft = at?.draftAnswers || [];
        if (draft.length) {
          const byId = {};
          qs.forEach((q, i) => (byId[q.id] = i));
          const seeded = {};
          draft.forEach((d) => {
            const qi = byId[d.questionId];
            if (qi != null) seeded[qi] = new Set(d.selectedOptionIds || []);
          });
          setAnswers(seeded);
        }
        // Countdown for timed quizzes, anchored on startedAt when available.
        if (at?.timeLimitMinutes) {
          startTimer(startedAtRef.current + at.timeLimitMinutes * 60000);
        }
      } else {
        setBlocked(
          message || t("learner.lms.quiz.unavailable", "This quiz is not available right now.")
        );
      }
    } catch (e) {
      globalAlert("error", e?.response?.data?.message);
    }
    setLoading(false);
  };

  const startTimer = (endsAt) => {
    const tick = () => {
      const left = Math.round((endsAt - Date.now()) / 1000);
      setRemaining(left);
      if (left <= 0) {
        stopTimer();
        // Auto-submit what's answered; the server stores late attempts as expired.
        if (!submittingRef.current) submitRef.current?.();
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
  };
  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setRemaining(null);
  };

  useEffect(() => {
    if (learnerMode) loadLearner();
    else if (serverMode) loadServer();
    else buildClient();
    return () => {
      stopTimer();
      if (draftTimer.current) clearTimeout(draftTimer.current);
    };
  }, [quizId, quiz, learnerQuizId]);

  const answersPayload = () =>
    questions.map((qq, qi) => ({
      questionId: qq.id,
      selectedOptionIds: [...(answers[qi] || [])],
    }));

  const optionLetter = (index) => String.fromCharCode(65 + index);

  const normalizeIds = (value) => {
    if (!value) return [];
    const list = Array.isArray(value) ? value : [value];
    return list
      .map((item) =>
        item?._id ||
        item?.id ||
        item?.optionId ||
        item?.selectedOptionId ||
        item
      )
      .filter(Boolean)
      .map(String);
  };

  const pickIds = (obj, keys) => {
    for (const key of keys) {
      const ids = normalizeIds(obj?.[key]);
      if (ids.length) return ids;
    }
    return [];
  };

  const buildFeedbackItem = (feedbackItem = {}, question, questionIndex) => {
    const selectedOptionIds =
      pickIds(feedbackItem, [
        "selectedOptionIds",
        "selectedOptions",
        "userSelectedOptionIds",
        "userAnswerIds",
        "answers",
      ]) || [];
    const fallbackSelectedIds = normalizeIds([...(answers[questionIndex] || [])]);
    const selectedIds = selectedOptionIds.length ? selectedOptionIds : fallbackSelectedIds;

    const correctIds = pickIds(feedbackItem, [
      "correctOptionIds",
      "correctOptions",
      "correctAnswerIds",
      "correctAnswers",
      "answerOptionIds",
    ]);
    const sourceOptions = feedbackItem.options?.length
      ? feedbackItem.options
      : question?.options || [];

    return {
      correct: !!(feedbackItem.isCorrect ?? feedbackItem.correct),
      questionText: feedbackItem.questionText || question?.questionText || "",
      explanation: feedbackItem.explanation || question?.explanation || "",
      hints: feedbackItem.hints || [],
      selectedOptionIds: selectedIds,
      options: sourceOptions.map((option, optionIndex) => {
        const id = String(option?._id || option?.id || option?.optionId || optionIndex);
        const isSelected = selectedIds.includes(id);
        const rawExplanation =
          option?.explanation ||
          option?.hint ||
          option?.reason ||
          option?.feedback ||
          "";
        return {
          id,
          text: option?.text || option?.optionText || option?.label || "",
          isSelected,
          isCorrect: correctIds.length
            ? correctIds.includes(id)
            : !!option?.isCorrect || (!!feedbackItem.isCorrect && selectedIds.includes(id)),
          explanation:
            rawExplanation ||
            (isSelected && !option?.isCorrect ? feedbackItem.hints?.[0] || "" : ""),
        };
      }),
    };
  };

  // Debounced draft autosave so a crash/refresh loses nothing (learner mode).
  const queueDraft = () => {
    if (!learnerMode || !attempt?.attemptId || result) return;
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      learnerQuizDraftApi(attempt.attemptId, answersPayloadRef.current()).catch(() => { });
    }, 1500);
  };
  const answersPayloadRef = useRef(answersPayload);
  answersPayloadRef.current = answersPayload;

  const total = questions.length;
  const q = questions[idx];

  const toggle = (optId) => {
    setAnswers((prev) => {
      const cur = new Set(prev[idx] || []);
      if (q.multi) {
        cur.has(optId) ? cur.delete(optId) : cur.add(optId);
      } else {
        cur.clear();
        cur.add(optId);
      }
      return { ...prev, [idx]: cur };
    });
    queueDraft();
  };

  const submit = async () => {
    const completedAt = new Date().toISOString();
    const timeTakenSeconds = Math.max(
      0,
      Math.round((Date.now() - startedAtRef.current) / 1000)
    );
    if (learnerMode) {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setSubmitting(true);
      stopTimer();
      if (draftTimer.current) clearTimeout(draftTimer.current);
      try {
        const res = await learnerQuizSubmitApi(
          attempt?.attemptId,
          answersPayloadRef.current(),
          enrolmentId
        );
        const { status, data, message } = res?.data || {};
        const r = data?.result;
        if (status === 200 && r) {
          const byId = {};
          questions.forEach((qq, qi) => (byId[qq.id] = { question: qq, index: qi }));
          setResult({
            percent: r.scorePercent,
            passed: r.passed,
            timedOut: !!r.timedOut,
            marksScored: r.marksScored,
            totalMarks: r.totalMarks,
            attemptNumber: attempt?.attemptNumber,
            completedAt,
            timeTakenSeconds,
            feedback: (r.feedback || []).map((f, fallbackIndex) => {
              const match = byId[f.questionId] || {
                question: questions[fallbackIndex],
                index: fallbackIndex,
              };
              return buildFeedbackItem(f, match.question, match.index);
            }),
          });
          if (r.passed) onComplete?.(true);
        } else {
          setBlocked(message || t("common.somethingWentWrong"));
        }
      } catch (e) {
        globalAlert("error", e?.response?.data?.message);
      }
      setSubmitting(false);
      submittingRef.current = false;
      return;
    }
    if (serverMode) {
      setSubmitting(true);
      try {
        const res = await lmsGradeQuizPreviewApi(quizId, answersPayload());
        const r = res?.data?.data?.result;
        if (r) {
          const byId = {};
          questions.forEach((qq, qi) => (byId[qq.id] = { question: qq, index: qi }));
          setResult({
            preview: !!(r.preview ?? res?.data?.data?.persisted === false),
            percent: r.scorePercent,
            passed: r.passed,
            completedAt,
            timeTakenSeconds,
            feedback: (r.feedback || []).map((f, fallbackIndex) => {
              const match = byId[f.questionId] || {
                question: questions[fallbackIndex],
                index: fallbackIndex,
              };
              return buildFeedbackItem(f, match.question, match.index);
            }),
          });
          if (r.passed) onComplete?.(true);
        } else globalAlert("error", res?.data?.message);
      } catch (e) {
        globalAlert("error", e?.response?.data?.message);
      }
      setSubmitting(false);
      return;
    }
    let totalMarks = 0;
    let scored = 0;
    const feedback = questions.map((qq, qi) => {
      totalMarks += qq.marks;
      const correctIds = qq.options.filter((o) => o.isCorrect).map((o) => o.id);
      const sel = [...(answers[qi] || [])];
      const correct = sameSet(sel, correctIds);
      if (correct) scored += qq.marks;
      const hints = qq.options
        .filter((o) => sel.includes(o.id) && o.hint)
        .map((o) => o.hint);
      return {
        ...buildFeedbackItem({ correct }, qq, qi),
        correct,
        questionText: qq.questionText,
        explanation: qq.explanation || "",
        hints,
      };
    });
    const percent = totalMarks ? Math.round((scored / totalMarks) * 100) : 0;
    const passed = percent >= passMark;
    setResult({ percent, passed, completedAt, timeTakenSeconds, feedback });
    if (passed) onComplete?.(true);
  };
  const submitRef = useRef(submit);
  submitRef.current = submit;

  const retake = () => {
    if (learnerMode) loadLearner();
    else if (serverMode) loadServer();
    else buildClient();
  };

  /* ── render ── */

  if (loading)
    return (
      <p className="text-muted">
        <i className="fas fa-circle-notch fa-spin mr-1"></i>
        {t("admin.lms.preview.loadingQuiz")}
      </p>
    );

  if (blocked)
    return (
      <div className="lms-quiz-blocked" role="alert">
        <i className="fas fa-lock mr-2"></i>
        {blocked}
      </div>
    );

  if (!total)
    return <p className="text-muted">{t("admin.lms.preview.quizEmpty")}</p>;

  if (result) {
    const feedback = result.feedback || [];
    const correctCount = feedback.filter((f) => f.correct).length;
    const incorrectCount = Math.max(feedback.length - correctCount, 0);
    const currentSolution = feedback[solutionIndex] || feedback[0] || {};
    const solutionOptions = currentSolution.options || [];
    const statusForOption = (option) => {
      if (option.isCorrect && option.isSelected) return "selected-correct";
      if (option.isCorrect) return "correct";
      if (option.isSelected) return "selected-wrong";
      return "normal";
    };
    const labelForOption = (state) => {
      if (state === "selected-correct") {
        return t("admin.lms.preview.yourCorrectAnswer", "Your Correct Answer");
      }
      if (state === "correct") {
        return t("admin.lms.preview.correctAnswer", "Correct Answer");
      }
      if (state === "selected-wrong") {
        return t("admin.lms.preview.yourAnswer", "Your Answer");
      }
      return "";
    };
    const iconForOption = (state) => {
      if (state === "correct" || state === "selected-correct") return "fa-check";
      if (state === "selected-wrong") return "fa-times";
      return "";
    };
    const explanationTitleForOption = (state) => {
      if (state === "correct" || state === "selected-correct") {
        return t(
          "admin.lms.preview.whyCorrect",
          "Why this answer is correct"
        );
      }
      if (state === "selected-wrong") {
        return t("admin.lms.preview.whyWrong", "Why this answer is wrong");
      }
      return "";
    };
    const shouldShowOptionExplanation = (option, state) =>
      !!option.explanation &&
      (state === "correct" ||
        state === "selected-correct" ||
        state === "selected-wrong");
    const scorePercent = Math.round(Number(result.percent) || 0);
    const resultState = result.passed ? "pass" : "fail";
    const previewResult = !!result.preview || previewMode;
    const resultLabel = previewResult
      ? t("admin.lms.preview.previewOnly", "Preview only")
      : result.passed
        ? t("admin.lms.preview.passed", "Passed")
        : result.timedOut
          ? t("learner.lms.quiz.timedOut", "Time ran out")
          : t("admin.lms.preview.notPassed", "Not Passed");
    const timeTaken = previewResult ? null : fmtElapsed(result.timeTakenSeconds);
    const completedAt = previewResult ? null : fmtTimestamp(result.completedAt);
    const stroke = 339.292;
    const scoreOffset = stroke - (stroke * Math.min(scorePercent, 100)) / 100;
    const openSolutionAt = (index) => {
      setSolutionIndex(index);
      setShowSolution(true);
    };

    return (
      <div className={`lms-preview-quiz lms-result-screen ${resultState}`}>
        <section className="lms-result-hero">
          <div className="lms-result-score-ring" style={{ "--score-offset": scoreOffset }}>
            <svg viewBox="0 0 120 120" aria-hidden="true">
              <circle className="lms-result-ring-track" cx="60" cy="60" r="54"></circle>
              <circle className="lms-result-ring-value" cx="60" cy="60" r="54"></circle>
            </svg>
            <div className="lms-result-ring-copy">
              <strong>{scorePercent}%</strong>
              <span>{t("admin.lms.preview.score", "Score")}</span>
            </div>
          </div>

          <div className="lms-result-hero-copy">
            <span className="lms-result-status-pill">
              <i
                className={`fas ${previewResult
                    ? "fa-eye"
                    : result.passed
                      ? "fa-circle-check"
                      : "fa-circle-xmark"
                  }`}
              ></i>
              {resultLabel}
            </span>
            <h3>
              {previewResult
                ? t("admin.lms.preview.resultTitle", "Preview result")
                : result.passed
                  ? t("admin.lms.preview.passResultTitle", "Assessment passed")
                  : t("admin.lms.preview.failResultTitle", "Assessment not passed")}
            </h3>
            <p>
              {previewResult
                ? t(
                  "admin.lms.preview.resultBody",
                  "This is an admin preview result only. No learner attempt or submission has been created."
                )
                : result.passed
                  ? t(
                    "admin.lms.preview.passResultBody",
                    "Great work. You have met the required passing percentage."
                  )
                  : t(
                    "admin.lms.preview.failResultBody",
                    "Review the solutions, then reattempt the quiz when you are ready."
                  )}
            </p>
            <div className="lms-result-meta">
              <span>
                <i className="fas fa-bullseye"></i>
                {t("admin.lms.preview.passMarkIs", { mark: passMark })}
              </span>
              {!previewResult && result.attemptNumber != null && (
                <span>
                  <i className="fas fa-rotate-right"></i>
                  {t("learner.lms.quiz.attemptN", "Attempt {{n}}", {
                    n: result.attemptNumber,
                  })}
                </span>
              )}
              {completedAt && (
                <span>
                  <i className="fas fa-calendar-check"></i>
                  {completedAt}
                </span>
              )}
            </div>
          </div>
        </section>

        <section className="lms-result-stats" aria-label="Quiz performance">
          <div className="lms-result-stat-card correct">
            <span><i className="fas fa-check"></i></span>
            <strong>{correctCount}</strong>
            <em>{t("admin.lms.preview.correctAnswers", "Correct Answers")}</em>
          </div>
          <div className="lms-result-stat-card incorrect">
            <span><i className="fas fa-times"></i></span>
            <strong>{incorrectCount}</strong>
            <em>{t("admin.lms.preview.incorrectAnswers", "Incorrect Answers")}</em>
          </div>
          {timeTaken && (
            <div className="lms-result-stat-card time">
              <span><i className="fas fa-clock"></i></span>
              <strong>{timeTaken}</strong>
              <em>{t("admin.lms.preview.timeTaken", "Time Taken")}</em>
            </div>
          )}
          <div className="lms-result-stat-card total">
            <span><i className="fas fa-clipboard-list"></i></span>
            <strong>{feedback.length}</strong>
            <em>{t("admin.lms.preview.totalQuestions", "Total Questions")}</em>
          </div>
        </section>

        <section className="lms-result-review-card">
          <div className="lms-result-review-head">
            <div>
              <h4>{t("admin.lms.preview.questionSummary", "Question Summary")}</h4>
              <p>{t("admin.lms.preview.questionSummaryHint", "Select any question to view its solution.")}</p>
            </div>
            <div className="lms-result-legend">
              <span><i className="correct"></i>{t("admin.lms.preview.correct", "Correct")}</span>
              <span><i className="incorrect"></i>{t("admin.lms.preview.incorrect", "Incorrect")}</span>
              {/* <span><i className="skipped"></i>{t("admin.lms.preview.notAttempted", "Not Attempted")}</span> */}
            </div>
          </div>

          <div className="lms-result-question-grid">
            {feedback.map((f, i) => {
              const state = !f.selectedOptionIds?.length
                ? "skipped"
                : f.correct
                  ? "correct"
                  : "incorrect";
              return (
                <button
                  className={`lms-result-question-chip ${state}`}
                  key={i}
                  onClick={() => openSolutionAt(i)}
                  type="button"
                >
                  <span>{i + 1}</span>
                  <strong>
                    {state === "correct"
                      ? t("admin.lms.preview.correct", "Correct")
                      : state === "incorrect"
                        ? t("admin.lms.preview.incorrect", "Incorrect")
                        : t("admin.lms.preview.notAttempted", "Not Attempted")}
                  </strong>
                  <i className="fas fa-chevron-right"></i>
                </button>
              );
            })}
          </div>

          <div className="lms-result-actions">
            <button
              className="lms-result-action-primary"
              onClick={retake}
              type="button"
              disabled={loading}
            >
              <i className="fas fa-rotate-right"></i>
              {previewResult
                ? t("admin.lms.preview.previewAgain", "Preview again")
                : t("admin.lms.preview.reattemptQuiz", "Reattempt Quiz")}
            </button>
            <button
              className="lms-result-action-secondary"
              onClick={() => openSolutionAt(0)}
              type="button"
              disabled={!feedback.length}
            >
              <i className="fas fa-list-check"></i>
              {t("admin.lms.preview.viewSolutions", "View Solutions")}
            </button>
            {/* {result.passed && (
              <button
                className="lms-result-action-tertiary"
                onClick={() => onComplete?.(true)}
                type="button"
              >
                <i className="fas fa-arrow-right"></i>
                {t("admin.lms.preview.continueCourse", "Continue Course")}
              </button>
            )} */}
          </div>
        </section>

        {showSolution && (
          <div className="lms-solution-modal-backdrop" role="presentation">
            <div
              className="lms-solution-modal-scrim"
              onClick={() => setShowSolution(false)}
            ></div>
            <button
              className="lms-solution-float-nav previous"
              disabled={solutionIndex === 0}
              onClick={() => setSolutionIndex((i) => Math.max(0, i - 1))}
              type="button"
              aria-label={t("admin.lms.preview.previous")}
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <div className="lms-solution-modal" role="dialog" aria-modal="true">
              <header className="lms-solution-header">
                <div>
                  <h3>{t("admin.lms.preview.viewSolution", "View Solution")}</h3>
                  <span>
                    {t("admin.lms.preview.questionOf", {
                      n: solutionIndex + 1,
                      total: feedback.length,
                    })}
                  </span>
                </div>
                <button
                  className="lms-solution-close"
                  onClick={() => setShowSolution(false)}
                  type="button"
                  aria-label={t("common.close", "Close")}
                >
                  <i className="fas fa-times"></i>
                </button>
              </header>

              <div className="lms-solution-content" key={solutionIndex}>
                <section className="lms-solution-question">
                  <span className="lms-solution-question-number">
                    {solutionIndex + 1}
                  </span>
                  <p>{currentSolution.questionText}</p>
                </section>

                <section className="lms-solution-options">
                  {solutionOptions.map((option, optionIndex) => {
                    const state = statusForOption(option);
                    const label = labelForOption(state);
                    const icon = iconForOption(state);
                    const explanationTitle = explanationTitleForOption(state);
                    const showExplanation = shouldShowOptionExplanation(option, state);
                    return (
                      <div className={`lms-solution-option ${state}`} key={option.id}>
                        <span className="lms-solution-option-index">
                          {optionLetter(optionIndex)}
                        </span>
                        <div className="lms-solution-option-body">
                          <span className="lms-solution-option-text">
                            {option.text}
                          </span>
                          {showExplanation && (
                            <div className="lms-solution-option-explanation">
                              <strong>{explanationTitle}</strong>
                              <p>{option.explanation}</p>
                            </div>
                          )}
                        </div>
                        {label && (
                          <span className="lms-solution-option-badge">
                            <i className={`fas ${icon}`}></i>
                            {label}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </section>

                {/* <section className="lms-solution-explanation">
                  <h4>{t("admin.lms.preview.solution", "Solution")}</h4>
                  <p>
                    {currentSolution.explanation ||
                      t(
                        "admin.lms.preview.noSolution",
                        "No explanation has been added for this question."
                      )}
                  </p>
                </section> */}
              </div>

              <footer className="lms-solution-footer">
                <div className="lms-solution-question-nav">
                  {feedback.map((f, i) => (
                    <button
                      className={`lms-solution-question-dot ${!f.selectedOptionIds?.length
                          ? "skipped"
                          : f.correct
                            ? "correct"
                            : "incorrect"
                        } ${i === solutionIndex ? "current" : ""}`}
                      key={i}
                      onClick={() => setSolutionIndex(i)}
                      type="button"
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              </footer>
            </div>
            <button
              className="lms-solution-float-nav next"
              disabled={solutionIndex >= feedback.length - 1}
              onClick={() =>
                setSolutionIndex((i) => Math.min(feedback.length - 1, i + 1))
              }
              type="button"
              aria-label={t("admin.lms.preview.next")}
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        )}
      </div>
    );
  }

  if (result?.__legacy) {
    return (
      <div className="lms-preview-quiz">
        <div className={`lms-preview-result ${result.passed ? "pass" : "fail"}`}>
          <div className="lms-preview-result-score">
            {Math.round(Number(result.percent) || 0)}%
          </div>
          <div className="lms-preview-result-label">
            {result.passed
              ? t("admin.lms.preview.passed")
              : result.timedOut
                ? t("learner.lms.quiz.timedOut", "Time ran out")
                : t("admin.lms.preview.failed")}
            <span className="lms-preview-result-sub">
              {t("admin.lms.preview.passMarkIs", { mark: passMark })}
              {result.marksScored != null && result.totalMarks != null
                ? ` · ${result.marksScored}/${result.totalMarks}`
                : ""}
            </span>
          </div>
        </div>
        <div className="lms-preview-feedback">
          {result.feedback.map((f, i) => (
            <div className="lms-preview-feedback-row" key={i}>
              <i
                className={`fas ${f.correct
                    ? "fa-check-circle text-success"
                    : "fa-times-circle text-danger"
                  } mr-2`}
              ></i>
              <div>
                <div className="lms-preview-fq">
                  {i + 1}. {f.questionText}
                </div>
                {(f.hints || []).map((h, hi) => (
                  <div className="small text-muted" key={hi}>
                    <i className="fas fa-lightbulb mr-1"></i>
                    {h}
                  </div>
                ))}
                {!f.correct && f.explanation && (
                  <div className="small text-muted">{f.explanation}</div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="d-flex justify-content-end mt-3">
          {(!learnerMode || !result.passed) && (
            <button className="btn secondary-btn btn-own-grey" onClick={retake}>
              <i className="fas fa-redo mr-1"></i>
              {t("admin.lms.preview.retake")}
            </button>
          )}
        </div>
      </div>
    );
  }

  const sel = answers[idx] || new Set();
  const selectedCount = sel.size;
  const answeredCount = questions.filter((_, questionIndex) => {
    return (answers[questionIndex] || new Set()).size > 0;
  }).length;
  const readyToSubmit = answeredCount === total && selectedCount > 0;
  const selectionInstruction = q.multi
    ? t("admin.lms.preview.selectAllApply", "Select all that apply")
    : t("admin.lms.preview.selectOneAnswer", "Select one answer");
  const statusClass = !selectedCount
    ? "empty"
    : readyToSubmit
      ? "ready"
      : "active";
  const statusText = !selectedCount
    ? t("admin.lms.preview.noAnswerSelected", "No Answer Selected")
    : readyToSubmit
      ? t("admin.lms.preview.readyToSubmit", "Ready to Submit")
      : t(
        selectedCount === 1
          ? "admin.lms.preview.answerSelected"
          : "admin.lms.preview.answersSelected",
        selectedCount === 1 ? "1 Answer Selected" : "{{count}} Answers Selected",
        { count: selectedCount }
      );
  const partTitle =
    quizMeta?.partTitle ||
    quizMeta?.title ||
    t("admin.lms.preview.partQuiz", "Part quiz");
  return (
    <div className="lms-preview-quiz">
      {/* Attempt meta chips (learner mode) */}
      {learnerMode && (
        <div className="lms-quiz-meta">
          <span className="lms-quiz-chip">
            <i className="fas fa-bullseye mr-1"></i>
            {t("admin.lms.preview.passMarkIs", { mark: passMark })}
          </span>
          {attempt?.attemptNumber != null && (
            <span className="lms-quiz-chip">
              <i className="fas fa-rotate-right mr-1"></i>
              {t("learner.lms.quiz.attemptN", "Attempt {{n}}", {
                n: attempt.attemptNumber,
              })}
              {quizMeta?.maxAttempts ? ` / ${quizMeta.maxAttempts}` : ""}
            </span>
          )}
          {remaining != null && (
            <span className={`lms-quiz-chip ${remaining < 60 ? "danger" : ""}`}>
              <i className="fas fa-clock mr-1"></i>
              {fmtClock(remaining)}
            </span>
          )}
          {attempt?.resumed && (
            <span className="lms-quiz-chip">
              <i className="fas fa-history mr-1"></i>
              {t("learner.lms.quiz.resumed", "Resumed")}
            </span>
          )}
        </div>
      )}

      <div className="lms-preview-quiz-panel">
        <div className="lms-preview-quiz-header">
          <div className="lms-preview-quiz-context">
            {quizMeta?.moduleBadge && (
              <>
                <span className="lms-preview-module-badge">
                  {quizMeta.moduleTitle}
                  {/* {quizMeta.moduleBadge} */}
                </span>
                <span className="lms-preview-module-badge">
                  {partTitle}
                </span>
                <span
                  className="lms-preview-qcount"
                  style={{ marginLeft: "auto" }}
                >
                  {t("admin.lms.preview.questionOf", { n: idx + 1, total })}
                </span>
              </>
            )}
            {/* {quizMeta?.moduleTitle && (
              <span className="lms-preview-module-title">
                {quizMeta.moduleTitle}
              </span>
            )} */}
          </div>
          {/* <div className="lms-preview-quiz-heading-row">
            <div>
              <h3>{partTitle}</h3>
              <p>{quizTitle}</p>
            </div>
            <span className="lms-preview-qcount">
              {t("admin.lms.preview.questionOf", { n: idx + 1, total })}
            </span>
          </div> */}
        </div>

        {/* <div className="lms-preview-quiz-progress-block"> */}
        {/* <div className="lms-preview-quiz-progress" aria-hidden="true">
            <span style={{ width: `${progressPercent}%` }}></span>
          </div> */}
        {/* <div className="lms-preview-quiz-progress-copy">
            <strong>{progressPercent}% Completed</strong>
            <span>{t("admin.lms.preview.questionOf", { n: idx + 1, total })}</span>
          </div> */}
        {/* </div> */}

        <div className="lms-preview-question-block">
          <span className="lms-preview-qtype-badge">
            <i className={`fas ${q.multi ? "fa-list-check" : "fa-circle-dot"}`}></i>
            {selectionInstruction}
          </span>
          <div className="lms-preview-qtext">{q.questionText}</div>
        </div>

        <div className="lms-preview-options">
          {q.options.map((o, optionIndex) => (
            <label
              key={o.id}
              className={`lms-preview-option ${sel.has(o.id) ? "selected" : ""}`}
            >
              <input
                type={q.multi ? "checkbox" : "radio"}
                name={`pq-${idx}`}
                checked={sel.has(o.id)}
                onChange={() => toggle(o.id)}
                className="lms-preview-option-input"
              />
              <span className="lms-preview-option-ripple" aria-hidden="true"></span>
              <span className="lms-preview-option-mark" aria-hidden="true">
                {sel.has(o.id) ? (
                  <i className="fas fa-check"></i>
                ) : (
                  String.fromCharCode(65 + optionIndex)
                )}
              </span>
              <span className="lms-preview-option-text">{o.text}</span>
            </label>
          ))}
        </div>
        <div className="lms-preview-quiz-actions">
          <button
            className="lms-preview-quiz-nav-btn"
            disabled={idx === 0}
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            type="button"
          >
            <i className="fas fa-chevron-left"></i>
            {t("admin.lms.preview.previous")}
          </button>
          <span className={`lms-preview-answer-status ${statusClass}`}>
            <i
              className={`fas ${statusClass === "ready"
                  ? "fa-check-circle"
                  : statusClass === "active"
                    ? "fa-circle-check"
                    : "fa-circle-info"
                }`}
            ></i>
            {statusText}
          </span>
          {idx < total - 1 ? (
            <button
              className="lms-preview-quiz-nav-btn primary"
              onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
              type="button"
            >
              {t("admin.lms.preview.next")}
              <i className="fas fa-chevron-right"></i>
            </button>
          ) : (
            <button
              className="lms-preview-submit-btn"
              onClick={submit}
              disabled={submitting || !selectedCount}
              title={
                selectedCount
                  ? t("admin.lms.preview.submit", "Submit")
                  : t("admin.lms.preview.selectAnswerFirst", "Select an answer first")
              }
              type="button"
            >
              {submitting ? (
                <>
                  <i className="fas fa-circle-notch fa-spin"></i>
                  {t("common.loading", "Loading...")}
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i>
                  {selectedCount
                    ? t("admin.lms.preview.submit", "Submit")
                    : t("admin.lms.preview.selectAnswerFirst", "Select an answer first")}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LmsQuizRunner;
